import React, { useEffect, useState } from "react";
import { Repeat, Plus, Trash2, Loader2, Play, Linkedin, Facebook, Instagram, Store, Power, Clock, Sparkles, Wand2, Image as ImageIcon } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ImageUpload from "./ImageUpload";
import NetworkPreview from "./NetworkPreview";
import { useLinkPreview, firstUrl } from "./useLinkPreview";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const NETWORKS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "google", label: "Google Business", icon: Store },
];
const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
// Recommended UK best posting slot per network (day_of_week 0=Mon, plus hour/min),
// derived from the curated windows in the backend /metricool/best-times.
const BEST_SLOT = {
  linkedin: { day_of_week: 1, hour: 9, minute: 0, label: "Tue 9:00am" },
  facebook: { day_of_week: 2, hour: 10, minute: 0, label: "Wed 10:00am" },
  instagram: { day_of_week: 3, hour: 12, minute: 0, label: "Thu 12:00pm" },
  google: { day_of_week: 1, hour: 10, minute: 0, label: "Tue 10:00am" },
};
const NET_ICON = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram, google: Store };
const NET_LABEL = { linkedin: "LinkedIn", facebook: "Facebook", instagram: "Instagram", google: "Google Business" };
const fmt = (d) => (d ? new Date(d).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const EMPTY = {
  name: "", text: "", image_url: "", networks: ["linkedin", "facebook"],
  cadence: "weekly", day_of_week: 1, day_of_month: 1, hour: 9, minute: 0,
  draft: true, active: true, first_comment: "", source: "custom", source_id: "",
};

export function PromoForm({ open, onClose, onSaved, editing, initial }) {
  const { toast } = useToast();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const genCaption = async (base, silent = false) => {
    setAiLoading(true);
    try {
      const { caption } = await adminApi.metricool.caption({
        kind: base?.kind || "post",
        title: base?.title || "",
        summary: base?.summary || "",
        link: base?.link || "",
        networks: base?.networks || ["linkedin", "facebook"],
        tone: "professional",
      });
      if (caption) setF((p) => ({ ...p, text: caption }));
    } catch (e) {
      if (!silent) toast({ title: "Couldn't draft a caption", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setAiLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    if (editing) { setF({ ...EMPTY, ...editing }); return; }
    if (initial) {
      const seed = {
        ...EMPTY,
        name: initial.name || initial.title || "",
        text: initial.text || "",
        image_url: initial.image_url || "",
        networks: initial.networks || ["linkedin", "facebook"],
        source: initial.source || "custom",
        source_id: initial.source_id || "",
      };
      setF(seed);
      if (initial.autogen && (initial.title || initial.summary)) genCaption(initial, true);
      return;
    }
    setF(EMPTY);
  }, [open, editing, initial]); // eslint-disable-line

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleNet = (k) => setF((p) => ({ ...p, networks: p.networks.includes(k) ? p.networks.filter((x) => x !== k) : [...p.networks, k] }));

  const applyBestTime = () => {
    const first = f.networks[0];
    const slot = BEST_SLOT[first];
    if (!slot) return;
    setF((p) => ({ ...p, cadence: p.cadence === "monthly" ? "weekly" : p.cadence, day_of_week: slot.day_of_week, hour: slot.hour, minute: slot.minute }));
    toast({ title: "Best time applied", description: `${NET_LABEL[first]}: ${slot.label} (UK)` });
  };

  const save = async () => {
    if (!f.name.trim()) { toast({ title: "Name your promo", variant: "destructive" }); return; }
    if (!f.text.trim()) { toast({ title: "Add the post text", variant: "destructive" }); return; }
    if (!f.networks.length) { toast({ title: "Pick a network", variant: "destructive" }); return; }
    if (f.networks.includes("instagram") && !f.image_url) { toast({ title: "Instagram needs an image", variant: "destructive" }); return; }
    setSaving(true);
    try {
      await adminApi.metricool.saveRecurring({ ...f, id: editing?.id });
      toast({ title: editing ? "Promo updated" : "Recurring promo created" });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast({ title: "Couldn't save", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const bestFirst = BEST_SLOT[f.networks[0]];
  const linkPreview = useLinkPreview(f.text, !!f.image_url);
  const linkNoImage = !f.image_url && !!firstUrl(f.text);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recurring-form">
        <DialogHeader>
          <DialogTitle className="font-display text-navy text-2xl flex items-center gap-2"><Repeat size={20} className="text-orange" /> {editing ? "Edit promo" : "New recurring promo"}</DialogTitle>
          <DialogDescription className="text-slate-500">Evergreen content that auto-posts to your Metricool planner on a schedule.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-navy font-semibold">Promo name</Label>
            <input data-testid="recurring-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Owner Independence tool" className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-navy font-semibold">Post text</Label>
              <button type="button" data-testid="recurring-ai-caption" onClick={() => genCaption({ title: f.name, summary: "", link: "", networks: f.networks })} disabled={aiLoading} className="inline-flex items-center gap-1.5 text-xs font-bold text-orange hover:text-orange-dark disabled:opacity-50">
                {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {f.text ? "Regenerate" : "Draft with AI"}
              </button>
            </div>
            <Textarea data-testid="recurring-text" value={f.text} onChange={(e) => set("text", e.target.value)} rows={4} placeholder="Write the evergreen caption…" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-navy font-semibold mb-1.5 block">Networks</Label>
            <div className="flex flex-wrap gap-2">
              {NETWORKS.map((n) => {
                const Icon = n.icon; const on = f.networks.includes(n.key);
                return (
                  <button key={n.key} type="button" data-testid={`recurring-net-${n.key}`} onClick={() => toggleNet(n.key)}
                    className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy/30"}`}>
                    <Icon size={15} /> {n.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-navy font-semibold">Image (optional)</Label>
            <div className="mt-1.5"><ImageUpload value={f.image_url} onChange={(v) => set("image_url", v)} hint="Required for Instagram." /></div>
          </div>

          {linkNoImage && (
            <div data-testid="recurring-image-reminder" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              <ImageIcon size={14} className="shrink-0 mt-0.5" />
              <span>No image attached — LinkedIn/Facebook will show a link-preview card using the linked page's share image (see Preview). Attach an image to post your own picture instead.</span>
            </div>
          )}

          {f.networks.length > 0 && (
            <div>
              <Label className="text-navy font-semibold mb-1.5 block">Preview</Label>
              <NetworkPreview text={f.text} image={f.image_url} networks={f.networks} linkPreview={linkPreview} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-navy font-semibold">Repeat</Label>
              <select data-testid="recurring-cadence" value={f.cadence} onChange={(e) => set("cadence", e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange/40">
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            {f.cadence === "monthly" ? (
              <div>
                <Label className="text-navy font-semibold">Day of month</Label>
                <select data-testid="recurring-dom" value={f.day_of_month} onChange={(e) => set("day_of_month", Number(e.target.value))} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange/40">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <Label className="text-navy font-semibold">Day</Label>
                <select data-testid="recurring-dow" value={f.day_of_week} onChange={(e) => set("day_of_week", Number(e.target.value))} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange/40">
                  {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-navy font-semibold">Time (UK)</Label>
              <input type="time" data-testid="recurring-time" value={`${String(f.hour).padStart(2, "0")}:${String(f.minute).padStart(2, "0")}`}
                onChange={(e) => { const [h, m] = e.target.value.split(":"); set("hour", Number(h)); set("minute", Number(m)); }}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-semibold text-navy pb-2">
                <input type="checkbox" data-testid="recurring-draft" checked={f.draft} onChange={(e) => set("draft", e.target.checked)} className="w-4 h-4 accent-orange" />
                {f.draft ? "Post as draft" : "Auto-publish live"}
              </label>
            </div>
          </div>

          {bestFirst && f.cadence !== "monthly" && (
            <button type="button" data-testid="recurring-best-time" onClick={applyBestTime} className="inline-flex items-center gap-1.5 text-xs font-bold text-navy hover:text-orange">
              <Wand2 size={13} /> Use best UK time for {NET_LABEL[f.networks[0]]} ({bestFirst.label})
            </button>
          )}

          <Button onClick={save} disabled={saving} data-testid="recurring-save" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving…</> : (editing ? "Save changes" : "Create promo")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// One-click button for content rows/cards: turn a blog post, podcast or
// diagnostic tool into a recurring promo, caption pre-filled (AI-drafted).
export function MetricoolRecurringButton({ initial, className, label = "Recurring" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={`metricool-recurring-btn-${initial?.source_id || "custom"}`}
        onClick={() => setOpen(true)}
        title="Set this up as a recurring promo"
        className={className || "h-9 px-3 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors bg-slate-100 text-navy hover:bg-slate-200"}
      >
        <Repeat size={14} /> {label}
      </button>
      <PromoForm open={open} onClose={() => setOpen(false)} initial={initial} />
    </>
  );
}


export default function RecurringPromos({ configured }) {
  const { toast } = useToast();
  const [promos, setPromos] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => adminApi.metricool.recurring().then(setPromos).catch(() => setPromos([]));
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    setBusyId(p.id);
    try { await adminApi.metricool.toggleRecurring(p.id); load(); }
    catch { toast({ title: "Couldn't update", variant: "destructive" }); }
    finally { setBusyId(null); }
  };

  const runNow = async (p) => {
    setBusyId(p.id);
    try {
      await adminApi.metricool.runRecurringNow(p.id);
      toast({ title: "Sent to Metricool", description: `“${p.name}” was pushed as ${p.draft ? "a draft" : "a live post"}.` });
    } catch (e) {
      toast({ title: "Couldn't send now", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setBusyId(null); }
  };

  const del = async () => {
    try { await adminApi.metricool.removeRecurring(deleteId); load(); toast({ title: "Promo deleted" }); }
    catch { toast({ title: "Couldn't delete", variant: "destructive" }); }
    finally { setDeleteId(null); }
  };

  const cadenceLabel = (p) => {
    if (p.cadence === "monthly") return `Monthly on day ${p.day_of_month}`;
    return `${p.cadence === "fortnightly" ? "Fortnightly" : "Weekly"} on ${DOW[p.day_of_week] || "Monday"}`;
  };

  return (
    <div className="mt-10" data-testid="recurring-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-navy flex items-center gap-2"><Repeat size={18} className="text-orange" /> Recurring promos</h2>
          <p className="text-sm text-slate-500">Auto-repost evergreen content to Metricool on a schedule.</p>
        </div>
        <Button data-testid="recurring-new" onClick={() => { setEditing(null); setFormOpen(true); }} disabled={!configured} className="bg-navy hover:bg-navy-deep text-white font-bold rounded-full px-5">
          <Plus size={16} className="mr-1" /> New promo
        </Button>
      </div>

      {promos.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-8 text-center text-slate-400"><Repeat size={28} className="mx-auto mb-2 text-slate-300" />No recurring promos yet.</div>
      ) : (
        <div className="space-y-3">
          {promos.map((p) => (
            <div key={p.id} data-testid={`recurring-item-${p.id}`} className={`bg-white rounded-xl border p-4 ${p.active ? "border-slate-100" : "border-slate-100 opacity-70"}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-navy">{p.name}</p>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.active ? "Active" : "Paused"}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.draft ? "bg-slate-100 text-slate-600" : "bg-orange/10 text-orange-dark"}`}>{p.draft ? "Draft" : "Auto-publish"}</span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mt-1">{p.text}</p>
                  <p className="text-xs text-slate-400 mt-2 flex items-center gap-2 flex-wrap">
                    {(p.networks || []).map((n) => { const NI = NET_ICON[n] || Repeat; return <span key={n} className="inline-flex items-center gap-1"><NI size={12} /> {NET_LABEL[n] || n}</span>; })}
                    <span className="text-slate-300">·</span> {cadenceLabel(p)} at {String(p.hour).padStart(2, "0")}:{String(p.minute).padStart(2, "0")}
                    <span className="text-slate-300">·</span> <Clock size={11} /> Next: {fmt(p.next_run_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`recurring-run-${p.id}`} onClick={() => runNow(p)} disabled={!configured || busyId === p.id} title="Send now" className="w-8 h-8 rounded-lg bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center text-emerald-600 disabled:opacity-40">{busyId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}</button>
                  <button data-testid={`recurring-toggle-${p.id}`} onClick={() => toggle(p)} disabled={busyId === p.id} title={p.active ? "Pause" : "Activate"} className={`w-8 h-8 rounded-lg flex items-center justify-center ${p.active ? "bg-amber-50 hover:bg-amber-100 text-amber-600" : "bg-slate-100 hover:bg-slate-200 text-slate-500"}`}><Power size={14} /></button>
                  <button data-testid={`recurring-edit-${p.id}`} onClick={() => { setEditing(p); setFormOpen(true); }} title="Edit" className="h-8 px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center text-xs font-semibold text-navy">Edit</button>
                  <button data-testid={`recurring-delete-${p.id}`} onClick={() => setDeleteId(p.id)} title="Delete" className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PromoForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} editing={editing} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="recurring-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this promo?</AlertDialogTitle>
            <AlertDialogDescription>It will stop auto-posting to Metricool. Posts already sent stay in your planner.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-500 hover:bg-red-600 rounded-full">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
